import React, { useCallback, useContext, useEffect, useState } from "react";
import { useStyles } from "./ComplianceBar.styles";
import { Box } from "@mui/system";
import { Avatar, Typography } from "@mui/material";
import { IComplianceBarProps } from "./ComplianceBarInterfaces";
import NoData from "components/NoData/NoData.view";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { ComplianceTypes } from "constants/constants";
import { RCAAPComplianceContext } from "contexts/RCAAPComplianceContext/RCAAPComplianceContext";

const ComplianceBar = ({ data, withBottomBorder, hidePercentage }: IComplianceBarProps) => {
    const [completedOnTimePercentage, setCompletedOnTimePercentage] = useState<number | null>(0);
    const [pendingPercentage, setPendingPercentage] = useState<number | null>(0);
    const [completedLatePercentage, setCompletedLatePercentage] = useState<number | null>(0);
    const [pendingLatePercentage, setPendingLatePercentage] = useState<number | null>(0);
    const { handleSetComplianceType } = useContext(RCAAPComplianceContext);
    const classes = useStyles();

    useEffect(() => {
        if (data?.length) {
            const completedOnTime = Math.ceil((Number(data[0]?.completedOnTimeAmount) / data[0]?.total) * 100);
            setCompletedOnTimePercentage(completedOnTime);
            const pending = Math.ceil((Number(data[0]?.pendingAmount) / data[0]?.total) * 100);
            setPendingPercentage(pending);
            const completedLate = Math.ceil((Number(data[0]?.completedLateAmount) / data[0]?.total) * 100);
            setCompletedLatePercentage(completedLate);
            const pendingLate = Math.ceil((Number(data[0]?.pendingLateAmount) / data[0]?.total) * 100);
            setPendingLatePercentage(pendingLate);
        }
    }, [data]);

    const handleSelectedComplianceType = useCallback(
        (selectedComplianceType: string) => {
            handleSetComplianceType(selectedComplianceType);
        },
        [handleSetComplianceType],
    );

    return (
        <Box className={classes.barContainer}>
            {data && data.length > 0 ? (
                <div className={`${classes.barWrapper} ${withBottomBorder && classes.border}`}>
                    {data[0]?.responsible?.id && (
                        <Box className={classes.avatarWrapper}>
                            {data[0]?.responsible?.media ? (
                                <Avatar
                                    src={data[0]?.responsible?.media}
                                    alt="user avatar"
                                    className={classes.avatar}
                                />
                            ) : (
                                <AccountCircleIcon className={classes.avatar} />
                            )}
                            {<Typography variant="error">{data[0]?.responsible?.name}</Typography>}
                        </Box>
                    )}
                    <Box
                        className={`${classes.progressBar} ${
                            data[0]?.responsible?.id ? classes.progressBarWithNameWidth : classes.progressBarWidth
                        }`}
                    >
                        {data[0]?.completedOnTimeAmount !== 0 && (
                            <Box
                                className={`${classes.completeOnTimeBar} ${classes.progressBarSection}`}
                                sx={{ width: `${completedOnTimePercentage}%` }}
                                onClick={() => handleSelectedComplianceType(ComplianceTypes.COMPLETED_ON_TIME)}
                            >
                                <span className={classes.amount}>
                                    <Typography variant="h5">{`${data[0]?.completedOnTimeAmount} ${
                                        hidePercentage ? "" : `(${completedOnTimePercentage}%)`
                                    }`}</Typography>
                                </span>
                            </Box>
                        )}
                        {data[0]?.pendingAmount !== 0 && (
                            <Box
                                className={`${classes.pendingBar} ${classes.progressBarSection}`}
                                sx={{ width: `${pendingPercentage}%` }}
                                onClick={() => handleSelectedComplianceType(ComplianceTypes.PENDING)}
                            >
                                {pendingPercentage && pendingPercentage > 0 && (
                                    <span className={classes.amount}>
                                        <Typography variant="h5">{`${data[0]?.pendingAmount} ${
                                            hidePercentage ? "" : `(${pendingPercentage}%)`
                                        }`}</Typography>
                                    </span>
                                )}
                            </Box>
                        )}
                        {data[0]?.completedLateAmount !== 0 && (
                            <Box
                                className={`${classes.completeLateBar} ${classes.progressBarSection}`}
                                sx={{ width: `${completedLatePercentage}%` }}
                                onClick={() => handleSelectedComplianceType(ComplianceTypes.COMPLETED_LATE)}
                            >
                                {completedLatePercentage && completedLatePercentage > 0 && (
                                    <span className={classes.amount}>
                                        <Typography variant="h5">{`${data[0]?.completedLateAmount} ${
                                            hidePercentage ? "" : `(${completedLatePercentage}%)`
                                        }`}</Typography>
                                    </span>
                                )}
                            </Box>
                        )}
                        {data[0]?.pendingLateAmount !== 0 && (
                            <Box
                                className={`${classes.pendingLateBar} ${classes.progressBarSection}`}
                                sx={{ width: `${pendingLatePercentage}%` }}
                                onClick={() => handleSelectedComplianceType(ComplianceTypes.PENDING_LATE)}
                            >
                                {pendingLatePercentage && pendingLatePercentage > 0 && (
                                    <span className={classes.amount}>
                                        <Typography variant="h5">{`${data[0]?.pendingLateAmount} ${
                                            hidePercentage ? "" : `(${pendingLatePercentage}%)`
                                        }`}</Typography>
                                    </span>
                                )}
                            </Box>
                        )}
                    </Box>
                </div>
            ) : (
                <Box className={classes.noData}>
                    <NoData />
                </Box>
            )}
        </Box>
    );
};

export default ComplianceBar;
